import { useState } from 'react'
import { useApp } from '../../context/AppContext'

/**
 * Componente de formulario para la creación de nuevas categorías de tickets.
 * * **Funcionalidades**:
 * - Captura el nombre y el estado de visibilidad (deleted) de una nueva categoría.
 * - Realiza la inserción directa en la tabla `category` dentro del esquema `Tickets` de Supabase.
 * - Gestiona estados de carga (loading) y feedback visual mediante mensajes de éxito o error.
 * - Notifica al componente padre mediante un callback una vez que la operación ha sido exitosa.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.onAdded - Función callback que se ejecuta tras insertar exitosamente una categoría (útil para refrescar listas).
 * * @returns {JSX.Element} Un formulario estilizado con Bootstrap para la gestión de categorías.
 */
const TicketCategoryAdd = ({ onAdded }) => {
  /** @type {Object} Cliente de Supabase obtenido del contexto de la aplicación. */
  const { supabase } = useApp()

  /** @type {[Object, Function]} Estado que almacena los datos del formulario de la nueva categoría. */
  const [newCategory, setNewCategory] = useState({
    name: '',
    deleted: false,
  })

  /** @type {[boolean, Function]} Estado que indica si la petición asíncrona está en curso. */
  const [loading, setLoading] = useState(false)

  /** @type {[string, Function]} Estado para mostrar retroalimentación (éxito/error) al usuario. */
  const [message, setMessage] = useState('')

  /**
   * Manejador genérico para cambios en los inputs del formulario.
   * Soporta tanto campos de texto como checkboxes.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de cambio de React.
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setNewCategory({
      ...newCategory,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  /**
   * Procesa el envío del formulario e inserta los datos en la base de datos.
   * @async
   * @param {React.FormEvent<HTMLFormElement>} e - Evento de envío del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    const { error } = await supabase
      .schema('Tickets')
      .from('category')
      .insert([newCategory])

    if (error) {
      setMessage(`Error: ${error.message}`)
    } else {
      setMessage('Categoría agregada correctamente')
      onAdded()
      //setNewCategory({ name: '', deleted: false })
    }

    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} className="tc-form">
      <div className="mb-3">
        <label className="tc-label" htmlFor="tc-add-name">
          Nombre
        </label>
        <input
          id="tc-add-name"
          type="text"
          name="name"
          className="form-control form-control-sm"
          placeholder="Ej: Soporte, Facturación, IT..."
          value={newCategory.name}
          onChange={handleChange}
          required
          autoFocus
        />
      </div>

      <div className="form-check mb-3">
        <input
          id="tc-add-deleted"
          type="checkbox"
          name="deleted"
          className="form-check-input"
          checked={newCategory.deleted}
          onChange={handleChange}
        />
        <label className="form-check-label small" htmlFor="tc-add-deleted">
          Marcar como eliminada
        </label>
      </div>

      {message && (
        <div
          className={`alert py-2 px-3 small mb-3 ${
            message.startsWith('Error') ? 'alert-danger' : 'alert-success'
          }`}
        >
          {message}
        </div>
      )}

      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fa-solid fa-plus me-2" />
              Agregar
            </>
          )}
        </button>
      </div>

      <style>{`
        .tc-label {
          display: block;
          margin-bottom: 4px;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--gm-text-muted);
        }
      `}</style>
    </form>
  )
}

export default TicketCategoryAdd
